import { FC, useMemo } from "react";
import { TaskRoute, TaskUpdateMarketInfo } from "tm-shared/types";

import { StepActionButton } from "@/domains/tasks";
import { useCompleteTask } from "@/domains/tasks/hooks";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import { useAppDispatch, useTaskPayloadSelector } from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { setMdTasksConfig } from "@/store/slices/ui";
import { getPhotoIdsFromPhotoTask, isSubtaskCompleted } from "@/utils";

interface StepButtonProps {
    task: TaskUpdateMarketInfo;
    pointId: string;
    currentCompleted: boolean;
    currentStep: number;
}

export const StepButton: FC<StepButtonProps> = ({
    task,
    pointId,
    currentCompleted = false,
    currentStep,
}) => {
    const dispatch = useAppDispatch();

    const taskId = task.id;
    const subtaskIds = task.context.subtasks.map((t) => t.subtaskId);

    const payloadMapBySubtaskId = useTaskPayloadSelector(
        TaskRoute.UpdateMarketInfo,
        taskId,
    );

    const allCompleted = useMemo(() => {
        if (!payloadMapBySubtaskId) {
            return false;
        }

        const allCompleted = Object.values(payloadMapBySubtaskId).every(
            isSubtaskCompleted,
        );

        return allCompleted;
    }, [payloadMapBySubtaskId]);

    const activeIndex = currentStep;
    const isLast = activeIndex === subtaskIds.length - 1;
    const buttonDisabled = isLast && !allCompleted && currentCompleted;

    const onNext = () => {
        if (isLast) return;

        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                field: "activeItem",
                value: String(activeIndex + 1),
            }),
        );
    };

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const completeTask = useCompleteTask({ targetId: pointId, taskId });

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        if (!payloadMapBySubtaskId) return;

        const photoIds = getPhotoIdsFromPhotoTask(payloadMapBySubtaskId);

        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: payloadMapBySubtaskId },
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,

            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });
    });

    return (
        <>
            {currentCompleted && (
                <StepActionButton
                    onNext={onNext}
                    onFinish={handleComplete}
                    isLast={isLast}
                    disabled={buttonDisabled}
                    loading={completeLoading}
                />
            )}

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
